import React from 'react';
import { cn } from '@/lib/utils';
import { SignalHigh, SignalLow, SignalMedium, Power, Pause } from 'lucide-react';
import { FanSpeed } from '@/types';

interface FanSpeedBadgeProps {
  fanSpeed: FanSpeed | null | undefined;
  className?: string;
}

export const fanSpeedConfig = {
  auto: { icon: Power, label: 'Auto' },
  low: { icon: SignalLow, label: 'Low' },
  med: { icon: SignalMedium, label: 'Med' },
  medium: { icon: SignalMedium, label: 'Med' },
  high: { icon: SignalHigh, label: 'High' },
  'ultra low': { icon: SignalLow, label: 'Ultra Low' },
  'ultra-low': { icon: SignalLow, label: 'Ultra Low' },
  'fan stop': { icon: Pause, label: 'Stop' },
  stop: { icon: Pause, label: 'Stop' },
} as const;

// Helper function to standardize fan speed values
const standardizeFanSpeed = (speed: string): string => {
  if (!speed) return '';

  // Convert to lowercase for case-insensitive comparison
  const lowerSpeed = speed.toLowerCase();

  // Map various forms to standardized values
  if (lowerSpeed === 'medium') return 'med';
  if (lowerSpeed === 'med') return 'med';
  if (lowerSpeed === 'ultra low') return 'ultra-low';
  if (lowerSpeed === 'ultra-low') return 'ultra-low';
  if (lowerSpeed === 'fan stop') return 'stop';
  if (lowerSpeed === 'stop') return 'stop';
  if (lowerSpeed === 'auto') return 'auto';
  if (lowerSpeed === 'low') return 'low';
  if (lowerSpeed === 'high') return 'high';

  // For any other value, just return the lowercase version
  return lowerSpeed;
};

export function FanSpeedBadge({ fanSpeed, className }: FanSpeedBadgeProps) {
  if (fanSpeed === null || fanSpeed === undefined) return <span className="text-dark-grey">-</span>;

  // Ensure fanSpeed is standardized to handle different formats
  const normalizedFanSpeed = standardizeFanSpeed(fanSpeed);

  // Find the config for this fan speed
  const configKey = Object.keys(fanSpeedConfig).find(key =>
    standardizeFanSpeed(key) === normalizedFanSpeed
  );

  const config = configKey ? fanSpeedConfig[configKey as keyof typeof fanSpeedConfig] : null;

  // If no config found, use a generic display with the fan speed name
  if (!config) {
    return (
      <div className={cn("flex items-center justify-center gap-1", className)}>
        <span className="w-5 h-5 flex items-center justify-center">
          <SignalMedium className="w-4 h-4 text-primary" />
        </span>
        <span className="paragraph-11 text-primary capitalize font-inter" style={{ fontFamily: 'Inter, sans-serif !important' }}>
          {fanSpeed}
        </span>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center justify-center gap-1", className)}>
      <span className="w-5 h-5 flex items-center justify-center">
        <config.icon className="w-4 h-4 text-primary" />
      </span>
      <span className="paragraph-11 text-primary capitalize font-inter" style={{ fontFamily: 'Inter, sans-serif !important' }}>{config.label}</span>
    </div>
  );
}
